/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import { LuActivity } from "react-icons/lu";

export const KpiCardNew = ({ label, val, color, icon: Icon, layout = 'vertical', statusColor, className }: any) => {
  const colorMap: any = {
    blue: 'text-blue-600 bg-blue-50 border-blue-100',
    emerald: 'text-emerald-600 bg-emerald-50 border-emerald-100',
    orange: 'text-orange-600 bg-orange-50 border-orange-100',
    rose: 'text-rose-600 bg-rose-50 border-rose-100',
    purple: 'text-purple-600 bg-purple-50 border-purple-100',
    amber: 'text-amber-600 bg-amber-50 border-amber-100',
    slate: 'text-slate-500 bg-slate-50 border-slate-100',
    indigo: 'text-indigo-600 bg-indigo-50 border-indigo-100',
  };

  const selectedColorClass = colorMap[color] || colorMap.blue;

  if (layout === 'hero') {
    return (
      <div className={`bg-white p-6 rounded-xl border border-slate-100 shadow-sm flex items-center gap-6 flex-1 hover:shadow-md transition-shadow cursor-pointer group ${className}`}>
        <div className={`w-20 h-20 rounded-full flex items-center justify-center ${selectedColorClass.split(' ')[1]} ${selectedColorClass.split(' ')[0]}`}>
           {Icon && <Icon className="w-10 h-10" />}
        </div>
        <div className="flex-1 text-center">
           <p className="text-sm font-bold text-slate-500 mb-1">{label}</p>
           <p className={`text-4xl font-black tracking-tight ${selectedColorClass.split(' ')[0]}`}>{val}</p>
        </div>
      </div>
    );
  }

  if (layout === 'horizontal') {
    return (
      <div className={`bg-white p-3 rounded-xl border-[3px] border-solid border-slate-200 shadow-sm flex items-center gap-4 hover:shadow-md transition-shadow cursor-pointer min-w-[180px] ${className}`}>
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${selectedColorClass.split(' ')[1]} ${selectedColorClass.split(' ')[0]}`}>
           {Icon && <Icon className="w-6 h-6" />}
        </div>
        <div>
           <p className="text-[13px] font-semibold text-slate-500 uppercase tracking-tight">{label}</p>
           <p className="text-xl font-black text-slate-800 tracking-tight">{val}</p>
        </div>
      </div>
    );
  }

  if (layout === 'mini') {
    return (
      <div className={`bg-white px-3 py-3 rounded-lg border border-slate-100 flex items-center gap-3 hover:bg-slate-50 transition-colors cursor-pointer group min-h-[125px] ${className}`}>
        {Icon && (
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${selectedColorClass.split(' ')[1]} ${selectedColorClass.split(' ')[0]}`}>
            <Icon className="w-4 h-4" />
          </div>
        )}
        <div className="flex-1">
          <p className="text-[13px] font-semibold text-slate-500 tracking-tight leading-tight">{label}</p>
          <p className={`text-lg font-[700] tracking-tight leading-tight ${statusColor || 'text-slate-800'}`}>{val}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white p-4 rounded-xl border border-slate-100 shadow-sm flex flex-col hover:shadow-md transition-shadow cursor-pointer ${className}`}>
       <div className="flex justify-between items-start mb-2">
          {Icon && <Icon className={`w-5 h-5 ${selectedColorClass.split(' ')[0]}`} />}
          <p className="text-[10px] font-bold text-slate-500 uppercase text-right leading-tight max-w-[80px]">{label}</p>
       </div>
       <p className={`text-2xl font-black text-slate-800 tracking-tight mt-auto ${statusColor || ''}`}>{val}</p>
    </div>
  );
};

type SectionHeaderNewProps = {
  title: string;
  onClick?: () => void;
};

export const SectionHeaderNew = ({
  title,
  onClick,
}: SectionHeaderNewProps) => (
  <div className={`flex items-center gap-3 mb-3 mt-4 ${
      onClick ? "cursor-pointer" : ""
    }`} onClick={onClick}>
    <div className="w-1.5 h-5 bg-blue-600 rounded-full" />
    <h3 className="m-0 text-[18px] font-semibold uppercase tracking-[0.03em] text-[#0F172A]">{title}</h3>
  </div>
);

export const InventoryHealthBar = ({ used, remaining, className }: { used: number, remaining: number, className?: string }) => {
  return (
    <div className={`bg-white px-6 rounded-xl border-[3px] border-solid border-slate-200 shadow-sm space-y-2 h-full flex flex-col justify-center ${className}`}>
      <div className="flex items-center gap-2">
         <LuActivity className="w-4 h-4 text-blue-600" />
         <h4 className="text-[10px] font-black uppercase tracking-widest text-blue-700">Inventory Health</h4>
      </div>
      <div>
        <div className="space-y-1">
          <div className="flex justify-between text-xs font-bold items-center">
            <span className="text-slate-600">Used Plates</span>
            <span className="text-emerald-600 text-lg font-black">{used}%</span>
          </div>
          <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
            <div  
              className="h-full bg-emerald-500 rounded-full transition-all duration-700 ease-out"
              style={{ width: `${used}%` }}
            />
          </div>
        </div>
        <div className="space-y-1">
          <div className="flex justify-between text-xs font-bold items-center">
            <span className="text-slate-600">Remaining Plates</span>
            <span className="text-blue-600 text-lg font-black">{remaining}%</span>
          </div>
          <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
            <div  
              className="h-full bg-blue-500 rounded-full transition-all duration-700 ease-out"
              style={{ width: `${remaining}%` }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};